import React, { FC, useMemo, useCallback, memo } from 'react';
import { useLazyQuery } from '@apollo/client';
import _map from 'lodash/map';
import { gqlRepresentationTypeEnum as REPRESENTATION_TYPE } from '@minecraft.graphql-types';
import ErrorMessage from '@minecraft.error-message';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import { FilterData } from '../../../shared/types';
import { SEARCH_ORGANIZATIONS } from '../FilterBasicCollectionGraphQL';

type Props = FilterData;

const CastingDirector: FC<Props> = ({ name, label }) => {
  const [getSearchOrganizarions, { data, loading, error, fetchMore }] = useLazyQuery(SEARCH_ORGANIZATIONS);

  const handleCastingDirectorFetch = useCallback((searchValue: string, offset, limit): void => {
    getSearchOrganizarions({
      variables: {
        input: {
          ...(searchValue && {
            orgName: searchValue,
          }),
          orgRepTypeCodes: [REPRESENTATION_TYPE.CASTING_COMPANY],
          page: {
            pageNumber: offset,
            pageSize: limit,
          },
        },
      },
    });
  }, []);

  const handleCastingDirectorFetchMore = useCallback(
    async (searchValue: string, offset: number, limit: number): Promise<void> => {
      await fetchMore({
        variables: {
          input: {
            ...(searchValue && {
              orgName: searchValue,
            }),
            orgRepTypeCodes: [REPRESENTATION_TYPE.CASTING_COMPANY],
            page: {
              pageNumber: offset,
              pageSize: limit,
            },
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            getOrganizationsByRepTypeAndName: {
              ...prev.getOrganizationsByRepTypeAndName,
              objects: [
                ...prev.getOrganizationsByRepTypeAndName.objects,
                ...fetchMoreResult.getOrganizationsByRepTypeAndName.objects,
              ],
            },
          };
        },
      });
    },
    [fetchMore]
  );

  const organizationsOptions = useMemo(() => {
    const organizationList = data?.getOrganizationsByRepTypeAndName?.objects;

    if (organizationList) {
      return _map(organizationList, (item) => ({ value: item.organization.id, label: item.organization.name }));
    }

    return [];
  }, [data?.getOrganizationsByRepTypeAndName?.objects]);

  if (error) return <ErrorMessage error={error} />;

  return (
    <DropdownWithChildren
      withFormik
      withSearch
      withAutocomplete
      isMultipleSelect
      isLoading={loading}
      initialOffset={1}
      initialLimit={20}
      name={name}
      toggleLabel={label}
      optionsTotalCount={data?.getOrganizationsByRepTypeAndName?.totalCount}
      options={organizationsOptions}
      onOptionsFetch={handleCastingDirectorFetch}
      onOptionsFetchMore={handleCastingDirectorFetchMore}
    />
  );
};

export default memo(CastingDirector);
